import React, { useState } from 'react';
import {
  Text,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { BoxRegular } from '@fluentui/react-icons';
import { ProductSearchInput, ProductGroup, StockItem, fullBinLabel } from './ProductSearchInput';
import { useProductSelection } from '../hooks/useProductSelection';
import {
  promotePhase,
  reconcilePhaseAfterLine,
  reservePhase,
  enqueuePhaseLineAdd,
  resolvePhaseLinePrice,
} from '../services/projectService';

// Cents → "$1,234.56". Local to the optimistic row; the resolved bin row uses
// the parent's own formatter.
const money = (cents: number) =>
  (cents / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' });

// Inline "add a bin" picker shown on an editable phase card so staff can drop a
// line item without navigating to the full order. Line items require a live
// Booqable order (the canonical /booqable/orders/<id>/lines path is the SOLE
// admin add-line route — see useProductSelection), so an un-promoted phase is
// promoted on first add. Pricing is re-resolved server-side via the same hook
// the order form uses, so tier authority stays in one place.

interface PhaseLineAddProps {
  phaseId: string;
  /** True once the phase has a Booqable order — skips the promote step. */
  promoted: boolean;
  startDate: string | null;
  endDate: string | null;
  /** Fired the instant the line lands in Booqable — BEFORE the background
   *  reconcile. The parent shows the bin optimistically (label + price) but must
   *  NOT refetch here: the mirror totals aren't reconciled yet, so a refetch
   *  would read stale 0-items/$0 and clobber the optimistic row back to the
   *  spinner. Passes the picked label + tier price (cents) for the optimistic row. */
  onAdded: (binLabel?: string, priceInCents?: number) => void;
  /** Fired once the background reconcile finishes (totals/extra-day/SKU settled).
   *  This is where the parent refetches to pull authoritative values. */
  onSettled?: () => void;
  /** Project-level default bin, chosen when the schedule was locked. Used as the
   *  bin to auto-add (see autoAdd); otherwise the card falls back to the search
   *  picker. */
  defaultBin?: { group: ProductGroup; stockItem: StockItem; label: string } | null;
  /** When true, add `defaultBin` to this phase automatically on mount (the "Lock
   *  & add bins" path) instead of waiting for a manual pick — fires once. */
  autoAdd?: boolean;
  /** Fired the moment an auto-add is kicked off, so the parent can drop this
   *  phase from its auto-add queue (a later manual remove must not re-add). */
  onAutoAddConsumed?: () => void;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('6px'),
  },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    ...shorthands.gap('8px'),
  },
  pickerWrap: {
    flexGrow: 1,
    minWidth: 0,
    // Drop the native select's own bottom margin so the picker's footprint
    // equals the bin slot's (40px box) — no height jump when the bin lands.
    '& select': {
      marginBottom: 0,
    },
  },
  hint: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  // Optimistic bin row shown the instant a product is picked — bordered slot
  // sized to match the dropdown box exactly, so nothing shifts on add.
  optimisticRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: '8px',
    width: '100%',
    minHeight: '40px',
    boxSizing: 'border-box',
    ...shorthands.padding('2px', '6px', '2px', '12px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
  },
  optimisticInfo: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    minWidth: 0,
  },
  optimisticIcon: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  optimisticTitle: {
    fontSize: '14px',
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Trailing cluster on the optimistic row: the resolved price (once probed) +
  // the saving spinner. Pushed to the right edge.
  optimisticTrailing: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    marginLeft: 'auto',
    flexShrink: 0,
  },
  optimisticPrice: {
    fontSize: '14px',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  // Faint background-saving cue that replaces the blocking spinner.
  optimisticSaving: {
    flexShrink: 0,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground4,
  },
});

// ProductSearchInput wants ISO datetimes for its availability window.
const toIso = (d: string | null, endOfDay = false): string | undefined =>
  d ? `${d.slice(0, 10)}T${endOfDay ? '23:59:59' : '00:00:00'}.000Z` : undefined;

const PhaseLineAdd: React.FC<PhaseLineAddProps> = ({
  phaseId,
  promoted,
  startDate,
  endDate,
  onAdded,
  onSettled,
  defaultBin,
  autoAdd,
  onAutoAddConsumed,
}) => {
  const styles = useStyles();
  const [promoteError, setPromoteError] = useState<string | null>(null);
  // The picked bin label. Set the instant a product is selected so the card shows
  // the item immediately; promote/add/reconcile then run in the background while
  // it's displayed. Cleared if the add fails (reverts to the picker). On success
  // the parent's refetch flips itemCount>0 and swaps in the real bin row. Seeded
  // for an auto-add so the card opens on the bin row, not a flash of the picker.
  const [optimisticLabel, setOptimisticLabel] = useState<string | null>(
    autoAdd && defaultBin ? defaultBin.label : null,
  );
  // Tier price shown in the optimistic row as soon as the (fast, DB-only)
  // resolver returns — usually well before the Booqable writes finish. Null
  // until probed / when the product can't be priced (Tier D, no address).
  const [optimisticPrice, setOptimisticPrice] = useState<number | null>(null);
  // Mirror of the picked label for the async callbacks, which capture their
  // render's scope — a ref always reads the value set at select time.
  const pendingLabelRef = React.useRef<string | undefined>(undefined);
  // Same, for the probed tier price (cents) — handed to the parent's optimistic
  // update so the bin's footer shows the price before the reconcile refetch.
  const pendingPriceRef = React.useRef<number | undefined>(undefined);
  // Guards the promote-on-open warm-up so it fires at most once per card mount.
  const prePromotedRef = React.useRef(false);
  // Guards the auto-add so it fires at most once per card mount.
  const autoFiredRef = React.useRef(false);

  const {
    handleProductSelect,
    tierResolving,
    callForPrice,
    lastError,
    clearError,
  } = useProductSelection({
    orderId: phaseId,
    // The add-line POST defers the mirror-total refresh (item count + price the
    // card reads) to reconcile-after-line — run it before refetching, else the
    // card refetches stale 0 items / $0. Runs in the background here: the bin
    // title is already on screen, so the user never waits on it.
    onLineAdded: () => {
      // The line exists in Booqable now, created at its tier price server-side.
      // Surface the bin optimistically (label + price) and DO NOT refetch — the
      // mirror totals aren't reconciled yet, so refetching here would read stale
      // 0-items/$0 and bounce the row back to the spinner. The follow-up
      // bookkeeping (SKU specify, Extra Day re-sync, pro forma, totals) runs in
      // the background; onSettled fires the single authoritative refetch when it
      // lands, so the user never watches those ~6s of Booqable round-trips.
      onAdded(pendingLabelRef.current, pendingPriceRef.current);
      // Reconcile, then reserve — a bin on a draft phase otherwise sits in
      // Booqable limbo (no committed inventory, perpetually-regenerating pro
      // forma). Reserve runs AFTER reconcile (so units are specified) and
      // BEFORE the single onSettled refetch, so the card lands on the true
      // status: reserved on success, or still-draft (which surfaces the card's
      // "Not reserved" retry) if reserve fails — e.g. no inventory for the
      // window. reservePhase is idempotent, so a later retry is safe.
      //
      // Enqueue this onto the SAME serialized chain as the adds. Each phase's
      // reconcile+reserve is ~7 Booqable order round-trips; firing them
      // fire-and-forget let several phases' tails overlap (Lock & add bins, or
      // quickly working Initial → Swap 1/2/3), bursting past Booqable's rate
      // limit and 429-ing the next foreground op. Queuing keeps the bursts from
      // forming (the #431 intent the add-line comment already claims) — the
      // optimistic row above is already on screen, so the queue wait is unseen.
      enqueuePhaseLineAdd(() =>
        reconcilePhaseAfterLine(phaseId, pendingLabelRef.current)
          .then(() => reservePhase(phaseId)),
      )
        .catch(() => {
          // Swallow: a failed reserve leaves the bin on a draft phase, which the
          // parent card flags with a Reserve action once the refetch lands.
        })
        .finally(() => {
          onSettled?.();
        });
    },
    // Revert the optimistic row to the picker so the user can retry.
    onLineAddFailed: () => {
      setOptimisticLabel(null);
      setOptimisticPrice(null);
    },
  });

  // Promote-on-open warm-up (#2): start minting the Booqable order the moment the
  // picker is focused, while the user is still scanning the dropdown — so the
  // ~order-creation latency overlaps their browsing instead of being paid after
  // the pick. Routed through the same serialized queue as adds so it can't burst
  // Booqable. Idempotent + best-effort: if it fails, onSelect's own promote
  // surfaces the error in context.
  const handlePickerActivate = () => {
    if (promoted || prePromotedRef.current) return;
    prePromotedRef.current = true;
    enqueuePhaseLineAdd(() => promotePhase(phaseId)).catch(() => {
      prePromotedRef.current = false;
    });
  };

  const onSelect = async (productGroup: ProductGroup, stockItem: StockItem) => {
    setPromoteError(null);
    clearError();
    // Show the bin immediately — everything below this runs in the background.
    const label = fullBinLabel(productGroup.name, stockItem.variation_name);
    pendingLabelRef.current = label;
    pendingPriceRef.current = undefined;
    setOptimisticLabel(label);
    setOptimisticPrice(null);
    // Show the tier price in the optimistic row ASAP (#1). A DB-only resolve with
    // no Booqable round-trip that works pre-promote, so the price typically
    // lands within a few hundred ms — long before the writes below finish. The
    // backend re-resolves the same value authoritatively on the actual add.
    const productId = stockItem.product_id || productGroup.id;
    resolvePhaseLinePrice(phaseId, productId).then((cents) => {
      if (cents != null) {
        pendingPriceRef.current = cents;
        setOptimisticPrice(cents);
      }
    });
    // Serialize the network sequence (promote → add-line → reconcile) across
    // every phase card so picking rolloffs for several phases at once doesn't
    // fan out concurrent Booqable writes and trip its rate limit (#431). The
    // optimistic row above is already on screen, so the queue wait is invisible.
    await enqueuePhaseLineAdd(async () => {
      // Mint the Booqable order on first add — line items can't attach to an
      // un-promoted draft. Revert (and surface the reason) if promotion fails,
      // e.g. a missing tax region, so we don't post a line to a phantom order.
      if (!promoted) {
        try {
          await promotePhase(phaseId);
        } catch (e) {
          setPromoteError(e instanceof Error ? e.message : 'Could not promote this phase.');
          setOptimisticLabel(null);
          return;
        }
      }
      await handleProductSelect(productGroup, stockItem);
    });
  };

  // Auto-add the default bin once on mount (the "Lock & add bins" path). Clears
  // itself from the parent's queue immediately so a later manual remove of this
  // bin doesn't trigger a re-add when the picker remounts.
  React.useEffect(() => {
    if (autoFiredRef.current || !autoAdd || !defaultBin) return;
    autoFiredRef.current = true;
    onAutoAddConsumed?.();
    onSelect(defaultBin.group, defaultBin.stockItem);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAdd, defaultBin]);

  // Optimistic state: the title is up the moment the user picks; the background
  // work (promote → add → reconcile → parent refetch) resolves underneath.
  if (optimisticLabel) {
    return (
      <div className={styles.root}>
        <div className={styles.optimisticRow}>
          <div className={styles.optimisticInfo}>
            <BoxRegular className={styles.optimisticIcon} />
            <Text className={styles.optimisticTitle}>{optimisticLabel}</Text>
          </div>
          <div className={styles.optimisticTrailing}>
            {optimisticPrice != null && (
              <Text className={styles.optimisticPrice}>{money(optimisticPrice)}</Text>
            )}
            {/* No blocking spinner — the row reads as settled the instant it's
                picked. A faint "Saving…" cue is the only sign the promote/add is
                still running in the background (and that a failure may revert it). */}
            <Text className={styles.optimisticSaving}>Saving…</Text>
          </div>
        </div>
        {(promoteError || lastError) && (
          <Text className={styles.error}>{promoteError || lastError}</Text>
        )}
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.row}>
        <div className={styles.pickerWrap}>
          <ProductSearchInput
            native
            nativeFullLabel
            size="medium"
            disabled={tierResolving || callForPrice}
            fromDate={toIso(startDate)}
            tillDate={toIso(endDate, true)}
            allowedProductTypes={['rental']}
            onPickerActivate={handlePickerActivate}
            onInventoryItemSelect={onSelect}
          />
        </div>
      </div>
      {callForPrice && (
        <Text className={styles.hint}>
          Tier D (call for price) — add this line in the full order.
        </Text>
      )}
      {(promoteError || lastError) && (
        <Text className={styles.error}>{promoteError || lastError}</Text>
      )}
    </div>
  );
};

export default PhaseLineAdd;
